/**
 * Statistics tracking and analysis module
 * Handles session stats, confusion matrix, and performance analysis
 */

import { MORSE_CODE, calculateMorseSimilarity } from './morse.js';
import { getCharacterMastery } from './spaced-repetition.js';

// Constants for accuracy calculations
export const ACCURACY_WINDOW_SIZE = 20;
export const EMA_ALPHA = 0.25; // Weight for new values in exponential moving average
export const ADAPTIVE_ALPHA = 0.4; // Higher alpha for quick response after speed changes
export const ADAPTIVE_WINDOW = 15; // Number of attempts to use adaptive alpha after speed change
export const COOLDOWN_ATTEMPTS = 12; // Number of attempts to disable recommendations after speed change

// Multi-window accuracy tracking
export const IMMEDIATE_WINDOW = 8;   // Very recent performance
export const SHORT_TERM_WINDOW = 25; // Current performance
export const LONG_TERM_WINDOW = 60;  // Stable baseline

// Training zone thresholds (accuracy %): below LOW is too hard, above HIGH is too easy
export const ZONE_LOW = 75;
export const ZONE_HIGH = 85;

/**
 * Initialize an empty confusion matrix
 * @param {string[]} characters - Array of characters to track
 * @returns {Object} Empty confusion matrix
 */
export function initializeConfusionMatrix(characters) {
    const matrix = {};
    characters.forEach(char => {
        matrix[char] = {};
    });
    return matrix;
}

/**
 * Update confusion matrix when a character is missed
 * @param {Object} confusionMatrix - Current confusion matrix
 * @param {string} missedChar - Character that was missed
 * @param {string} confusedWith - Character it was confused with
 * @returns {Object} Updated confusion matrix
 */
export function updateConfusionMatrix(confusionMatrix, missedChar, confusedWith) {
    if (!confusionMatrix[missedChar]) {
        confusionMatrix[missedChar] = {};
    }

    if (!confusionMatrix[missedChar][confusedWith]) {
        confusionMatrix[missedChar][confusedWith] = 0;
    }

    confusionMatrix[missedChar][confusedWith]++;

    return confusionMatrix;
}

/**
 * Analyze what character the user might have confused the missed character with
 * @param {string} missedChar - Character that was missed
 * @param {Object} patternGroups - Pattern groups for confusion analysis
 * @returns {{char: string, reason: string, weight: number}[]} Confusion candidates
 */
export function analyzeConfusion(missedChar, patternGroups) {
    const candidates = [];

    // Check pattern groups for known confusion pairs
    Object.values(patternGroups).forEach(group => {
        if (group.pairs) {
            group.pairs.forEach(pair => {
                if (pair.includes(missedChar)) {
                    const confusedWith = pair.find(c => c !== missedChar);
                    if (confusedWith) {
                        candidates.push({
                            char: confusedWith,
                            reason: 'Common confusion pair',
                            weight: 3
                        });
                    }
                }
            });
        }
    });

    // Check for similar morse patterns
    const missedCode = MORSE_CODE[missedChar];
    if (missedCode) {
        Object.entries(MORSE_CODE).forEach(([char, code]) => {
            if (char !== missedChar) {
                const similarity = calculateMorseSimilarity(missedCode, code);
                if (similarity > 0.7) {
                    candidates.push({
                        char: char,
                        reason: 'Similar pattern',
                        weight: similarity * 2
                    });
                }
            }
        });
    }

    // Sort by weight
    candidates.sort((a, b) => b.weight - a.weight);

    return candidates;
}

/**
 * Track a practice attempt
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {string} char - Character attempted
 * @param {boolean} correct - Whether attempt was correct
 * @param {number} maxWindow - Maximum window size
 * @returns {Array} Updated recent attempts
 */
export function trackAttempt(recentAttempts, char, correct, maxWindow = 20) {
    const attempt = {
        char,
        correct,
        timestamp: Date.now()
    };

    const updated = [...recentAttempts, attempt];

    // Maintain window size
    if (updated.length > maxWindow) {
        return updated.slice(-maxWindow);
    }

    return updated;
}

/**
 * Calculate rolling accuracy from recent attempts
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {number} windowSize - Window size to calculate over
 * @returns {number} Accuracy percentage (0-100)
 */
export function calculateRollingAccuracy(recentAttempts, windowSize) {
    const window = recentAttempts.slice(-windowSize);

    if (window.length === 0) return 0;

    const correct = window.filter(a => a.correct).length;
    return Math.round((correct / window.length) * 100);
}

/**
 * Calculate exponential moving average accuracy
 * Gives more weight to recent attempts, smoothing out streaks
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {number} windowSize - Maximum attempts to consider
 * @param {number} alpha - Smoothing factor (0-1), higher = more weight on recent
 * @returns {number} Accuracy percentage (0-100)
 */
export function calculateEMAAccuracy(recentAttempts, windowSize = ACCURACY_WINDOW_SIZE, alpha = EMA_ALPHA) {
    const window = recentAttempts.slice(-windowSize);
    
    if (window.length === 0) return 0;
    
    // Start with the first attempt
    let ema = window[0].correct ? 100 : 0;
    
    // Apply exponential smoothing to subsequent attempts
    for (let i = 1; i < window.length; i++) {
        const currentValue = window[i].correct ? 100 : 0;
        ema = alpha * currentValue + (1 - alpha) * ema;
    }
    
    return Math.round(ema);
}

/**
 * Calculate adaptive exponential moving average accuracy
 * Uses higher alpha (more reactive) shortly after speed changes
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {number} attemptsSinceChange - Attempts since last recognition time change
 * @param {number} windowSize - Maximum attempts to consider
 * @returns {number} Accuracy percentage (0-100)
 */
export function calculateAdaptiveEMAAccuracy(recentAttempts, attemptsSinceChange, windowSize = ACCURACY_WINDOW_SIZE) {
    const window = recentAttempts.slice(-windowSize);
    
    if (window.length === 0) return 0;
    
    // Use adaptive alpha if we recently changed recognition time
    const alpha = attemptsSinceChange < ADAPTIVE_WINDOW ? ADAPTIVE_ALPHA : EMA_ALPHA;
    
    // Start with the first attempt
    let ema = window[0].correct ? 100 : 0;
    
    // Apply exponential smoothing to subsequent attempts
    for (let i = 1; i < window.length; i++) {
        const currentValue = window[i].correct ? 100 : 0;
        ema = alpha * currentValue + (1 - alpha) * ema;
    }
    
    return Math.round(ema);
}

/**
 * Calculate accuracy across multiple time windows
 * @param {Array} recentAttempts - Array of recent attempts
 * @returns {{immediate: number, shortTerm: number, longTerm: number}}
 */
export function calculateMultiWindowAccuracy(recentAttempts) {
    return {
        immediate: calculateRollingAccuracy(recentAttempts, IMMEDIATE_WINDOW),
        shortTerm: calculateRollingAccuracy(recentAttempts, SHORT_TERM_WINDOW),
        longTerm: calculateRollingAccuracy(recentAttempts, LONG_TERM_WINDOW)
    };
}


/**
 * Calculate trend from accuracy history
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {number} windowSize - Size of window to analyze
 * @returns {{direction: string, strength: number}} Trend direction ('up'|'down'|'stable') and strength (0-1)
 */
export function calculateAccuracyTrend(recentAttempts, windowSize = 20) {
    if (recentAttempts.length < 5) {
        return { direction: 'stable', strength: 0 };
    }
    
    const window = recentAttempts.slice(-windowSize);
    
    // Calculate accuracy for each quarter of the window
    const quarterSize = Math.floor(window.length / 4);
    const q1 = calculateRollingAccuracy(window.slice(0, quarterSize), quarterSize);
    const q2 = calculateRollingAccuracy(window.slice(quarterSize, quarterSize * 2), quarterSize);
    const q3 = calculateRollingAccuracy(window.slice(quarterSize * 2, quarterSize * 3), quarterSize);
    const q4 = calculateRollingAccuracy(window.slice(quarterSize * 3), quarterSize);
    
    // Simple linear regression on quarters
    const n = 4;
    const sumX = 10; // 1+2+3+4
    const sumY = q1 + q2 + q3 + q4;
    const sumXY = 1*q1 + 2*q2 + 3*q3 + 4*q4;
    const sumX2 = 30; // 1+4+9+16
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    
    // Determine trend direction and strength
    let direction = 'stable';
    let strength = Math.abs(slope) / 100; // Normalize to 0-1
    
    if (slope > 5) direction = 'up';
    else if (slope < -5) direction = 'down';
    
    return { direction, strength: Math.min(1, strength) };
}


/**
 * Calculate robust accuracy using multiple techniques
 * @param {Array} recentAttempts - Array of recent attempts
 * @param {number} attemptsSinceChange - Attempts since last speed change
 * @returns {Object} Comprehensive accuracy data
 */
export function calculateRobustAccuracy(recentAttempts, attemptsSinceChange = null) {
    // Multi-window accuracies
    const windows = calculateMultiWindowAccuracy(recentAttempts);
    
    // Calculate trend
    const trend = calculateAccuracyTrend(recentAttempts, SHORT_TERM_WINDOW);
    
    // Use adaptive accuracy as primary metric
    const primaryAccuracy = calculateAdaptiveEMAAccuracy(
        recentAttempts, 
        attemptsSinceChange || 100, 
        SHORT_TERM_WINDOW
    );
    
    // Determine if we should show recommendations
    // Only if both short and long term are consistently out of zone
    const shortTermZone = getPerformanceZone(windows.shortTerm).zone;
    const longTermZone = getPerformanceZone(windows.longTerm).zone;
    const isConsistentlyOutOfZone = shortTermZone === longTermZone && shortTermZone !== 'learning';
    
    // Confidence based on sample size
    const sampleSize = Math.min(recentAttempts.length, SHORT_TERM_WINDOW);
    const confidence = Math.min(1, sampleSize / SHORT_TERM_WINDOW);
    
    return {
        accuracy: primaryAccuracy,              // Main display accuracy
        windows,                                // Multi-window accuracies
        trend,                                  // Trend analysis
        isConsistentlyOutOfZone,               // Should we recommend?
        confidence,                             // Confidence in measurement
        sampleSize                              // Number of attempts
    };
}

/**
 * Get performance zone based on accuracy
 * @param {number} accuracy - Accuracy percentage
 * @returns {{zone: string, message: string, class: string}}
 */
export function getPerformanceZone(accuracy) {
    if (accuracy < ZONE_LOW) {
        return {
            zone: 'too-hard',
            message: 'Consider increasing recognition time',
            class: 'danger-color'
        };
    } else if (accuracy <= ZONE_HIGH) {
        return {
            zone: 'learning',
            message: 'Perfect difficulty level for optimal learning',
            class: 'success-color'
        };
    } else {
        return {
            zone: 'too-easy',
            message: 'Consider decreasing recognition time',
            class: 'accent-blue'
        };
    }
}

/**
 * Calculate session statistics
 * @param {Object} sessionStats - Current session stats
 * @returns {Object} Calculated statistics
 */
export function calculateSessionStats(sessionStats) {
    const total = sessionStats.correct + sessionStats.incorrect;
    const accuracy = total > 0 ? Math.round((sessionStats.correct / total) * 100) : 0;
    const avgTimePerCharMs = total > 0 ? Math.round(sessionStats.elapsedTimeMs / total) : 0;

    return {
        ...sessionStats,
        total,
        accuracy,
        avgTimePerCharMs
    };
}

/**
 * Format time in mm:ss format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time
 */
export function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

/**
 * Get recommendation based on robust performance analysis
 * @param {Object} robustData - Data from calculateRobustAccuracy
 * @param {number} currentTime - Current recognition time
 * @param {number} attemptCount - Number of attempts
 * @param {number} attemptsSinceChange - Attempts since last recognition time change
 * @returns {{show: boolean, action: string, delta: number, message: string, cooldown: boolean, confidence: string}}
 */
export function getSpeedRecommendationRobust(robustData, currentTime, attemptCount, attemptsSinceChange = null) {
    // Don't show recommendations until we have enough data
    if (attemptCount < 15 || robustData.confidence < 0.5) {
        return { show: false };
    }

    // Check if we're in cooldown period after a speed change
    if (attemptsSinceChange !== null && attemptsSinceChange < COOLDOWN_ATTEMPTS) {
        return { show: false };
    }

    // Only recommend if consistently out of zone AND trend supports it
    if (!robustData.isConsistentlyOutOfZone) {
        return { show: false };
    }

    // Calculate actual adjustment values
    const adjustments = calculateRecognitionAdjustments(currentTime);
    const { windows, trend } = robustData;
    
    // Too easy: high accuracy in both windows AND stable/improving trend
    if (windows.shortTerm > ZONE_HIGH && windows.longTerm > ZONE_HIGH &&
        currentTime > 0 && trend.direction !== 'down') {
        return {
            show: true,
            action: 'decrease',
            delta: adjustments.decrease,
            message: `Consistently too easy - decrease by ${adjustments.decrease}ms`,
            cooldown: false,
            confidence: robustData.confidence > 0.8 ? 'high' : 'medium'
        };
    } 
    // Too hard: low accuracy in both windows AND not improving
    else if (windows.shortTerm < ZONE_LOW && windows.longTerm < ZONE_LOW &&
             currentTime < 3000 && trend.direction !== 'up') {
        return {
            show: true,
            action: 'increase',
            delta: adjustments.increase,
            message: `Consistently too hard - increase by ${adjustments.increase}ms`,
            cooldown: false,
            confidence: robustData.confidence > 0.8 ? 'high' : 'medium'
        };
    }

    return { show: false };
}

/**
 * Get character heat map data
 * @param {string[]} characters - All characters
 * @param {Object} charStats - Character statistics
 * @returns {Array} Heat map data
 */
export function getHeatMapData(characters, charStats) {
    return characters.map(char => {
        const stats = charStats[char] || { correct: 0, incorrect: 0 };
        const total = stats.correct + stats.incorrect;
        const accuracy = total > 0 ? Math.round((stats.correct / total) * 100) : 0;
        const mastery = getCharacterMastery(stats);

        return {
            char,
            stats,
            total,
            accuracy,
            mastery,
            difficulty: stats.incorrect - stats.correct
        };
    });
}

/**
 * Calculate recognition time adjustments based on current setting
 * @param {number} currentTime - Current recognition time in ms
 * @returns {{decrease: number, increase: number}} Adjustment values in ms
 */
export function calculateRecognitionAdjustments(currentTime) {
    // Calculate 13% adjustment, rounded to nearest 50ms
    const percentage = 0.13;
    const rawAdjustment = currentTime * percentage;
    
    // Round to nearest 50ms
    const roundedAdjustment = Math.round(rawAdjustment / 50) * 50;
    
    // Apply min/max constraints
    const adjustment = Math.max(50, Math.min(250, roundedAdjustment));
    
    return {
        decrease: adjustment,
        increase: adjustment
    };
}

/**
 * Get character priority scores for focus queue
 * @param {Object} charStats - Character statistics
 * @param {Object} confusionMatrix - Confusion matrix
 * @returns {Array} Sorted array of {char, score, accuracy, confusionCount, needsReview}
 */
export function getCharacterPriorityScores(charStats, confusionMatrix) {
    const now = Date.now();
    const scores = [];
    
    Object.entries(charStats).forEach(([char, stats]) => {
        const totalAttempts = stats.correct + stats.incorrect;
        if (totalAttempts === 0) return; // Skip unpracticed
        
        // Include characters that need review according to SM-2
        if (stats.nextReview <= now) {
            const accuracy = (stats.correct / totalAttempts) * 100;
            
            // Skip characters with perfect accuracy
            if (accuracy >= 100) return;
            
            scores.push({
                char,
                score: stats.easeFactor, // Lower = more difficult
                accuracy: Math.round(accuracy),
                totalAttempts,
                needsReview: true,
                confusionCount: Object.values(confusionMatrix[char] || {})
                    .reduce((sum, count) => sum + count, 0)
            });
        }
    });
    
    // Sort by easeFactor (ascending) - most difficult first
    return scores.sort((a, b) => a.score - b.score);
}

